import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ChevronDown } from 'lucide-react';
import { ProgressStepper, PAYMENT_STEPS } from '../components/ProgressStepper';
import { usePaymentParams, useUpdatePaymentParams } from './PaymentRouter';
import { canEditAmount } from '../lib/urlParams';
import { fetchRateQuote } from '../lib/api';

interface Network {
  id: string;
  name: string;
  assets: string[];
}

const NETWORKS: Network[] = [
  { id: 'TRC20', name: 'Tron (TRC20)', assets: ['USDT'] },
  { id: 'POLYGON', name: 'Polygon', assets: ['USDT', 'USDC'] },
  { id: 'BASE', name: 'Base', assets: ['USDT', 'USDC'] },
  { id: 'ETHEREUM', name: 'Ethereum', assets: ['USDT', 'USDC'] },
  { id: 'ARBITRUM', name: 'Arbitrum', assets: ['USDT', 'USDC'] },
  { id: 'OPTIMISM', name: 'Optimism', assets: ['USDT', 'USDC'] },
  { id: 'AVALANCHE', name: 'Avalanche', assets: ['USDT', 'USDC'] },
  { id: 'BNB', name: 'BNB Chain', assets: ['USDT', 'USDC'] },
  { id: 'CELO', name: 'Celo', assets: ['USDT', 'USDC', 'CUSD'] },
];

const COUNTRIES = [
  { code: 'NG', name: 'Nigeria', currency: 'NGN', flag: '🇳🇬' },
  { code: 'KE', name: 'Kenya', currency: 'KES', flag: '🇰🇪' },
  { code: 'GH', name: 'Ghana', currency: 'GHS', flag: '🇬🇭' },
  { code: 'ZA', name: 'South Africa', currency: 'ZAR', flag: '🇿🇦' },
  { code: 'UG', name: 'Uganda', currency: 'UGX', flag: '🇺🇬' },
];

export function AmountPage() {
  const navigate = useNavigate();
  const params = usePaymentParams();
  const updateParams = useUpdatePaymentParams();

  // Form state
  const [type, setType] = useState<'onramp' | 'offramp'>(params?.type || 'onramp');
  const [countryCode, setCountryCode] = useState(params?.countryCode || 'NG');
  const [network, setNetwork] = useState(params?.network || 'TRC20');
  const [asset, setAsset] = useState(params?.asset || 'USDT');
  const [amount, setAmount] = useState(params?.amount?.toString() || '');
  const [currency] = useState<'local' | 'crypto'>(params?.currency || 'local');
  const [quote, setQuote] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedCountry = COUNTRIES.find(c => c.code === countryCode);
  const selectedNetwork = NETWORKS.find(n => n.id === network);
  const localCurrency = selectedCountry?.currency || 'NGN';

  // Fetch quote when amount or any quote-affecting parameter changes
  useEffect(() => {
    if (amount && parseFloat(amount) > 0) {
      fetchQuote();
    }
  }, [amount, currency, network, asset, countryCode, type]);

  async function fetchQuote() {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchRateQuote({
        type,
        amount,
        fiatCurrency: localCurrency,
        countryCode,
      });
      setQuote(data);
    } catch (err: any) {
      console.error('Quote fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleContinue() {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    // Update params with user selections
    updateParams({
      type,
      network,
      asset,
      amount: parseFloat(amount),
      currency,
      countryCode
    });

    // Navigate to wallet page
    navigate('/pay/wallet');
  }

  const amountEditable = !params || canEditAmount(params);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Stepper */}
      <ProgressStepper
        steps={PAYMENT_STEPS}
        currentStep="amount"
        completedSteps={[]}
      />

      <div className="max-w-2xl mx-auto px-4 pb-8">
        {/* Custom branding */}
        {params?.logoUrl && (
          <div className="text-center mb-6">
            <img
              src={params.logoUrl}
              alt="Logo"
              className="h-12 w-auto mx-auto"
            />
          </div>
        )}

        {params?.title && (
          <h1 className="text-2xl font-bold text-gray-900 text-center mb-6">
            {params.title}
          </h1>
        )}

        {/* Main card */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* Type selector */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setType('onramp')}
              className={`
                flex-1 py-3 px-4 rounded-lg font-semibold transition-all
                ${type === 'onramp'
                  ? 'bg-brand text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              Buy Crypto
            </button>
            <button
              onClick={() => setType('offramp')}
              className={`
                flex-1 py-3 px-4 rounded-lg font-semibold transition-all
                ${type === 'offramp'
                  ? 'bg-brand text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              Sell Crypto
            </button>
          </div>

          {/* Country selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            <div className="relative">
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand outline-none appearance-none bg-white"
              >
                {COUNTRIES.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.name} ({country.currency})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Amount input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {type === 'onramp' ? 'You Pay' : 'You Receive'}
            </label>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold text-gray-700 px-3">
                {localCurrency}
              </span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                disabled={!amountEditable}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand outline-none text-lg font-semibold disabled:bg-gray-100"
              />
            </div>
            {!amountEditable && (
              <p className="text-sm text-gray-500 mt-1">Amount is fixed for this payment link</p>
            )}
          </div>

          {/* Network selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Network
            </label>
            <div className="relative">
              <select
                value={network}
                onChange={(e) => {
                  setNetwork(e.target.value);
                  const newNetwork = NETWORKS.find(n => n.id === e.target.value);
                  if (newNetwork && !newNetwork.assets.includes(asset)) {
                    setAsset(newNetwork.assets[0]);
                  }
                }}
                className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand outline-none appearance-none bg-white"
              >
                {NETWORKS.map(net => (
                  <option key={net.id} value={net.id}>
                    {net.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Asset selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Asset
            </label>
            <div className="flex gap-2">
              {selectedNetwork?.assets.map(assetOption => (
                <button
                  key={assetOption}
                  onClick={() => setAsset(assetOption)}
                  className={`
                    flex-1 py-2 px-4 rounded-lg font-medium transition-all
                    ${asset === assetOption
                      ? 'bg-brand text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }
                  `}
                >
                  {assetOption}
                </button>
              ))}
            </div>
          </div>

          {/* Quote display */}
          {quote && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">
                  {type === 'onramp' ? 'You Receive' : 'You Pay'}
                </span>
                <span className="text-lg font-bold text-gray-900">
                  {quote.cryptoAmount ? Number(quote.cryptoAmount).toFixed(4) : '0.0000'} {asset}
                </span>
              </div>
              <div className="flex justify-between items-center mt-2 text-sm">
                <span className="text-gray-500">Exchange Rate</span>
                <span className="text-gray-700">
                  1 {asset} ≈ {quote.rate ? Number(quote.rate).toLocaleString() : '0'} {localCurrency}
                </span>
              </div>
              {quote.feePercent && (
                <div className="flex justify-between items-center mt-1 text-sm">
                  <span className="text-gray-500">Fee</span>
                  <span className="text-gray-700">{quote.feePercent}%</span>
                </div>
              )}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Continue button */}
          <button
            onClick={handleContinue}
            disabled={loading || !amount || parseFloat(amount) <= 0}
            className="w-full py-4 px-6 bg-brand text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-opacity"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Loading...
              </>
            ) : (
              'Continue to Wallet'
            )}
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Powered by{' '}
            <a
              href="https://pouchfinance.xyz"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand hover:text-brand-600 font-medium"
            >
              PouchPay
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
